import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import { getBrukerDisplayName } from '@klp/user/utils';
import { useCurrentUser } from '@klp/auth';
import type { Aktivitet } from '@klp/formgenerator/interfaces';
import {
    findCamundaProperty,
    findCamundaPropertyValue,
} from '@klp/formgenerator/utils';
import { useMemo } from 'react';
import i18n from '@forvalter/i18n/config';

export type RolleKommentar = {
    activityId: string;
    user: string;
    rolle: string;
    timestamp: string;
    isReply: boolean;
    depth: number;
    comment: string;
};

const MAX_CAMUNDA_COMMENT_LENGTH = 4000;

const shortId = (id: string | undefined | null): string =>
    id?.slice(0, 8) ?? '';

const parse = (raw: string): Array<RolleKommentar> => {
    if (!raw) return [];
    return raw
        .split('\n')
        .map((line) => {
            const segments = line.split('|');
            // Support both old (5-segment) and new (6-segment) formats
            // The depth field (6th segment) stores: 0 = top-level, 1 = reply, 2 = reply-to-reply
            if (segments.length >= 6) {
                const [activityId, user, rolle, timestamp, depthStr, ...rest] =
                    segments;
                const depth = parseInt(depthStr, 10) || 0;
                return {
                    activityId: shortId(activityId?.trim()),
                    user,
                    rolle,
                    timestamp,
                    isReply: depth > 0,
                    depth,
                    comment: rest.join('|'),
                };
            }
            const [activityId, user, rolle, timestamp, ...rest] = segments;
            return {
                activityId: shortId(activityId?.trim()),
                user,
                rolle,
                timestamp,
                isReply: false,
                depth: 0,
                comment: rest.join('|'),
            };
        })
        .filter((c) => c.activityId && c.user && c.rolle && c.timestamp);
};

const serialize = (comments: Array<RolleKommentar>): string =>
    comments
        .map(
            ({ activityId, user, rolle, timestamp, depth, comment }) =>
                `${activityId}|${user}|${rolle}|${timestamp}|${depth}|${comment}`,
        )
        .join('\n');

export const useRolleKommentar = (activity: Aktivitet) => {
    const { currentUser } = useCurrentUser();
    const userName =
        getBrukerDisplayName(currentUser?.fulltnavn, currentUser?.epost) ||
        'Unknown user';
    const activityId = shortId(activity.id);

    const isRolleKommentarerFieldInTheForm = useMemo(
        () =>
            findCamundaProperty(
                CamundaFields.ROLLE_KOMMENTARER,
                activity.formFields ?? [],
            ),
        [activity.formFields],
    );

    const rawValue = useMemo(
        () =>
            findCamundaPropertyValue(
                CamundaFields.ROLLE_KOMMENTARER,
                activity.formFields ?? [],
            ) || '',
        [activity.formFields],
    );

    const allComments = useMemo(() => parse(rawValue), [rawValue]);

    const attachComment = (
        newComment: string,
        rolle: string,
        depth = 0,
        afterIndex?: number,
    ): string => {
        if (!newComment.trim()) return serialize(allComments);

        const newEntry: RolleKommentar = {
            activityId,
            user: userName,
            rolle,
            timestamp: new Date().toISOString(),
            isReply: depth > 0,
            depth,
            comment: newComment.trim(),
        };

        let comments: Array<RolleKommentar>;
        if (depth > 0 && afterIndex !== undefined) {
            const insertAt = afterIndex + 1;
            comments = [
                ...allComments.slice(0, insertAt),
                newEntry,
                ...allComments.slice(insertAt),
            ];
        } else {
            comments = [...allComments, newEntry];
        }

        const updated = serialize(comments);

        if (updated.length > MAX_CAMUNDA_COMMENT_LENGTH) {
            throw new Error(
                i18n.t(
                    `leverandor.${CamundaFields.ROLLE_KOMMENTARER}.field.error.commentTooLong`,
                ),
            );
        }

        return updated;
    };

    const maxCommentLength = useMemo(() => {
        const newEntry: RolleKommentar = {
            activityId,
            user: userName,
            rolle: '',
            timestamp: new Date().toISOString(),
            isReply: false,
            depth: 0,
            comment: '',
        };

        const updated = serialize([...allComments, newEntry]);
        return MAX_CAMUNDA_COMMENT_LENGTH - updated.length;
    }, [allComments, activityId, userName]);

    const removeComment = (index: number): string => {
        const comments = allComments.filter((_, i) => i !== index);
        return serialize(comments);
    };

    return {
        allComments,
        isRolleKommentarerFieldInTheForm,
        attachComment,
        removeComment,
        maxCommentLength,
    };
};
